import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { apisApi } from '../api/apisApi';
import type { ApiFilters, ApiFormValues } from '../types/api';

export function useApisQuery(filters: ApiFilters, page: number) {
  return useQuery({
    queryKey: ['gateway', 'apis', filters, page],
    queryFn: () => apisApi.list({ ...filters, page, size: 20, sort: 'createdAt,desc' }),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useApisSummary() {
  const total = useQuery({
    queryKey: ['gateway', 'apis', 'summary', 'total'],
    queryFn: () => apisApi.list({ page: 0, size: 1 }),
    staleTime: 60_000,
    select: (data) => data.totalElements,
  });
  const active = useQuery({
    queryKey: ['gateway', 'apis', 'summary', 'active'],
    queryFn: () => apisApi.list({ status: 'active', page: 0, size: 1 }),
    staleTime: 60_000,
    select: (data) => data.totalElements,
  });
  return {
    totalApis: total.data ?? 0,
    activeApis: active.data ?? 0,
    isLoading: total.isLoading || active.isLoading,
  };
}

export function useApiQuery(id: string | null) {
  return useQuery({
    queryKey: ['gateway', 'api', id],
    queryFn: () => apisApi.get(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useAuthPolicyOptions() {
  return useQuery({
    queryKey: ['gateway', 'authPolicies'],
    queryFn: () => apisApi.listAuthPolicies(),
    staleTime: 5 * 60_000,
  });
}

export function useRateLimitOptions() {
  return useQuery({
    queryKey: ['gateway', 'rateLimitPolicies'],
    queryFn: () => apisApi.listRateLimitPolicies(),
    staleTime: 5 * 60_000,
  });
}

export function useCreateApi(
  onSuccess?: (name: string) => void,
  onError?: (err: unknown) => void,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ApiFormValues) => apisApi.create(body),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['gateway', 'apis'] });
      onSuccess?.(created.name);
    },
    onError,
  });
}

export function useReplaceApi(
  id: string,
  onSuccess?: () => void,
  onError?: (err: unknown) => void,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ApiFormValues) => apisApi.replace(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gateway', 'apis'] });
      qc.invalidateQueries({ queryKey: ['gateway', 'api', id] });
      onSuccess?.();
    },
    onError,
  });
}

export function usePatchApi() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<ApiFormValues> }) =>
      apisApi.patch(id, body),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['gateway', 'apis'] });
      qc.invalidateQueries({ queryKey: ['gateway', 'api', id] });
    },
  });
}

/**
 * #450 (UAT 2026-05-14): onError 콜백 지원 — confirm 다이얼로그가 토스트로 에러 메시지를 노출하기 위함.
 */
export function useDeleteApi(
  onSuccess?: () => void,
  onError?: (err: unknown) => void,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apisApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gateway', 'apis'] });
      onSuccess?.();
    },
    onError,
  });
}

/**
 * #450 (UAT 2026-05-14): onError 콜백 지원.
 */
export function useDeprecateApi(
  onSuccess?: () => void,
  onError?: (err: unknown) => void,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apisApi.deprecate(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['gateway', 'apis'] });
      qc.invalidateQueries({ queryKey: ['gateway', 'api', id] });
      onSuccess?.();
    },
    onError,
  });
}

/**
 * #473 (UAT 2026-06-02): API 자동수집 mutation.
 * POST /api/eraf/gateway/apis/discover → DiscoverSummary.
 * 성공 시 API 목록 + 요약 쿼리 무효화.
 * ROLE_EXEC 는 BE 가 423 반환 → onError 로 흐름.
 */
export function useDiscoverApis(
  onSuccess?: (summary: import('../types/api').DiscoverSummary) => void,
  onError?: (err: unknown) => void,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apisApi.discover(),
    onSuccess: (summary) => {
      qc.invalidateQueries({ queryKey: ['gateway', 'apis'] });
      qc.invalidateQueries({ queryKey: ['gateway', 'apis', 'summary', 'total'] });
      qc.invalidateQueries({ queryKey: ['gateway', 'apis', 'summary', 'active'] });
      onSuccess?.(summary);
    },
    onError,
  });
}
